import { useMutation } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import DoNotDisturb from "@mui/icons-material/DoNotDisturb";
import FileDownload from "@mui/icons-material/FileDownload";
import Handshake from "@mui/icons-material/Handshake";
import ScienceIcon from "@mui/icons-material/Science";
import { Box, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { omit } from "lodash-es";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { gql } from "../../../../generated-graphql";
import { TenantsQuery } from "../../../../generated-graphql/graphql";
import { CreateTenant } from "./CreateTenant";
import { DownloadTierIIDeliverablesDialog } from "./DownloadTierIIDeliverablesDialog";
type Row = TenantsQuery["tenants"]["items"][0];

const UPDATE_TENANT = gql(`
  mutation UpdateTenant($input: TenantInput!) {
    upsertTenant(input: $input) {
      id
      isDisabled
    }
  }
`);

export const TENANTS_QUERY = gql(`
  query Tenants($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    tenants(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
        isDisabled
        isDemo
        isPartner
        partnerTenant {
          id
          name
        }
      }
      count
    }
  }
`);

export function OrganizationTable() {
  const { isStaff, isPartner } = useCurrentUser();
  const navigate = useNavigate();
  const alerts = useAlerts();
  const [showDialog, setShowDialog] = useState<
    "disable" | "enable" | "generateDeliverables" | undefined
  >(undefined);
  const [selectedTenant, setSelectedTenant] = useState<Row | undefined>(undefined);

  const [updateTenant, { loading: updatingTenant }] = useMutation(UPDATE_TENANT);

  const viewOrganizationUrl = useCallback(
    (tenantId: string) => {
      if (isStaff) {
        return `/staff/implementation/${tenantId}`;
      } else if (isPartner) {
        return `/o/${tenantId}/facilities`;
      }
      return null;
    },
    [isPartner, isStaff]
  );

  const viewOrganization = useCallback(
    (tenantId: string) => {
      const url = viewOrganizationUrl(tenantId);
      if (url) {
        navigate(url);
      }
    },
    [navigate, viewOrganizationUrl]
  );

  const columns: OmnisearchGridColDef<Row>[] = [
    {
      field: "name",
      headerName: "Organization",
      flex: 0.75,
    },
    {
      field: "isPartner",
      headerName: "Partner",
      flex: 0.25,
      filterKeyType: "boolean",
      renderCell: (params) => {
        return params.value ? <Handshake /> : null;
      },
    },
    {
      field: "isDisabled",
      headerName: "Disabled",
      flex: 0.25,
      filterKeyType: "boolean",
      renderCell: (params) => {
        return params.value ? "🛑" : "";
      },
    },
    {
      field: "isDemo",
      headerName: "Demo",
      flex: 0.2,
      filterKeyType: "boolean",
      renderCell: (params) => {
        return params.value ? <ScienceIcon /> : null;
      },
    },
    {
      field: "partnerTenant",
      headerName: "Partner Organization",
      flex: 0.75,
      valueGetter: (_, row) => {
        return row.partnerTenant?.name ?? "";
      },
    },
    {
      field: "actions",
      type: "actions",
      resizable: false,
      sortable: false,
      getActions: (params) => {
        const actions = [];
        if (params.row.isDisabled) {
          actions.push(
            <Tooltip title="Enable Tenant" key={1}>
              <GridActionsCellItem
                onClick={() => {
                  setShowDialog("enable");
                  setSelectedTenant(params.row);
                }}
                label="Enable Tenant"
                icon={<Check />}
              />
            </Tooltip>
          );
        } else {
          actions.push(
            <Tooltip title="Disable Tenant" key={1}>
              <GridActionsCellItem
                onClick={() => {
                  setShowDialog("disable");
                  setSelectedTenant(params.row);
                }}
                label="Disable Tenant"
                icon={<DoNotDisturb />}
              />
            </Tooltip>
          );
        }

        actions.push(
          <Tooltip title="Generate Deliverables" key={2}>
            <GridActionsCellItem
              onClick={() => {
                setShowDialog("generateDeliverables");
                setSelectedTenant(params.row);
              }}
              label="Generate Deliverables"
              icon={<FileDownload />}
            />
          </Tooltip>
        );
        return actions;
      },
    },
  ];

  const onClose = useCallback(() => {
    setShowDialog(undefined);
    setSelectedTenant(undefined);
  }, []);

  return (
    <Box py={3}>
      <>
        <OmnisearchDataGrid
          persistenceKey="organization-table"
          isURLDriven
          dataQuery={TENANTS_QUERY}
          columns={columns}
          getCount={(data) => data.tenants.count}
          getItems={(data) => data.tenants.items}
          onRowClick={(params) => viewOrganization(params.row.id)}
          getRowLink={(row) => viewOrganizationUrl(row.id)}
          commandButtons={[<CreateTenant key="create-tenant" />]}
        />
        {(showDialog === "disable" || showDialog === "enable") && (
          <ConfirmDialog
            open={!!showDialog}
            header={`Are you sure you want to ${showDialog} this tenant?`}
            confirmText={`Yes, ${showDialog}`}
            resources={[
              {
                resourceType: "Tenant",
                resourceName: selectedTenant?.name,
              },
            ]}
            loading={updatingTenant}
            onClose={onClose}
            onConfirm={async () => {
              if (selectedTenant == null) return;
              try {
                await updateTenant({
                  variables: {
                    input: {
                      ...omit(selectedTenant, "__typename", "partnerTenant"),
                      isDisabled: showDialog === "disable",
                    },
                  },
                });
                alerts.success(`Successfully updated ${selectedTenant.name}`);
                onClose();
              } catch (err) {
                alerts.error("Error updating tenant", { error: err });
              }
            }}
          />
        )}

        {selectedTenant && showDialog === "generateDeliverables" && (
          <DownloadTierIIDeliverablesDialog
            open={!!showDialog}
            onClose={onClose}
            tenantId={selectedTenant.id}
          />
        )}
      </>
    </Box>
  );
}
